import PageHeader from "../Components/PageHeader";
import Section from "../Components/Section";
import Card from "../Components/Card";
import { FaRegLightbulb, FaRegHandshake } from "react-icons/fa6";
import { LuSettings } from "react-icons/lu";
import { FaPeopleGroup } from "react-icons/fa6";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";

export default function LightCurrent() {
  useChangeTitle("Light Current Services");

  const lightCurrentFeatures = [
    {
      index: "1",
      title: "Smart Integration",
      desc: "Converting buildings to smart interactive systems with automatic and remote controls for enhanced functionality.",
      icon: <FaRegLightbulb />,
    },
    {
      index: "2",
      title: "Security Systems",
      desc: "Comprehensive security solutions including CCTV, access control, and time attendance systems.",
      icon: <FaRegHandshake />,
    },
    {
      index: "3",
      title: "Communication Networks",
      desc: "Advanced data networks, IPTV, MATV, and public address systems for seamless communication.",
      icon: <LuSettings />,
    },
    {
      index: "4",
      title: "Fire Safety",
      desc: "State-of-the-art fire alarm systems and BMS integration for comprehensive building safety.",
      icon: <FaPeopleGroup />,
    },
  ];

  return (
    <>
      <PageHeader heading="Light Current Systems" />
      
      {/* Hero Section with Tech Theme */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/services/light current.jpg" 
            alt="Light Current Systems" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 via-blue-800/80 to-indigo-900/90"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <div className="animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              Smart Building
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
                Intelligence
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-purple-100 max-w-4xl mx-auto leading-relaxed">
              Transforming spaces with integrated low-voltage technologies, security systems, 
              and intelligent automation that create truly smart, interactive environments.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-2xl">
                Smart Building Consultation
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-purple-800 px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300">
                Security Assessment
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Services Overview with Smart Tech Design */}
      <Section 
        title="Our Light Current Services" 
        desc="Comprehensive intelligent building solutions for the connected world"
        className="py-24 bg-gradient-to-br from-white via-purple-50 to-white relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-32 h-32 bg-purple-100 rounded-full -translate-x-16 -translate-y-16 opacity-50"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-blue-200 rounded-full translate-x-24 translate-y-24 opacity-30"></div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          {[
            { 
              title: "Fire Alarm Systems", 
              desc: "Advanced fire detection and alarm systems with centralized monitoring and automatic response.",
              icon: "🔥",
              color: "from-red-500 to-orange-500"
            },
            { 
              title: "CCTV & Security", 
              desc: "High-definition surveillance systems with remote monitoring and intelligent analytics.",
              icon: "📹",
              color: "from-purple-500 to-blue-500"
            },
            { 
              title: "Access Control", 
              desc: "Secure entry systems with time attendance tracking and biometric authentication.",
              icon: "🔒",
              color: "from-green-500 to-teal-500"
            },
            { 
              title: "Public Address", 
              desc: "Professional sound systems for announcements, music, and emergency communications.",
              icon: "📢",
              color: "from-blue-500 to-indigo-500"
            },
            { 
              title: "IPTV & MATV", 
              desc: "Internet Protocol Television and Master Antenna Television distribution systems.",
              icon: "📺",
              color: "from-indigo-500 to-purple-500"
            },
            { 
              title: "BMS Integration", 
              desc: "Building Management Systems for centralized control of all building systems.",
              icon: "🏢",
              color: "from-purple-500 to-pink-500"
            },
            { 
              title: "Data Networks", 
              desc: "Structured cabling and network infrastructure for high-speed data transmission.",
              icon: "🌐",
              color: "from-cyan-500 to-blue-500"
            },
            { 
              title: "Smart Controls", 
              desc: "Automated building controls with mobile app integration and remote management.",
              icon: "📱",
              color: "from-teal-500 to-green-500"
            },
            { 
              title: "Security Gates", 
              desc: "Automated security gate systems with vehicle detection and access management.",
              icon: "🚪",
              color: "from-gray-500 to-blue-500"
            }
          ].map((service, index) => (
            <div key={index} className="group relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-purple-400 via-blue-500 to-purple-600 rounded-2xl blur opacity-0 group-hover:opacity-25 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${service.color} rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300`}>
                      <span className="text-white text-xl">{service.icon}</span>
                    </div>
                    <h3 className="font-bold text-xl text-gray-800 group-hover:text-purple-600 transition-colors">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{service.desc}</p>
                </div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-purple-50 rounded-tl-full opacity-50"></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Smart Building Technologies with Futuristic Design */}
      <div className="py-24 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 border border-purple-400 rounded-full opacity-20"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 border border-blue-400 rounded-full opacity-30"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-purple-500 rounded-full opacity-10"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Smart Building Technologies</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Transform your building into an intelligent, interactive environment with cutting-edge automation and control systems.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <h3 className="text-3xl font-bold mb-8 text-white">Building Automation Features</h3>
                <div className="space-y-6">
                  {[
                    { icon: "💡", title: "Automated Lighting Control", desc: "Smart lighting systems with scheduling and presence detection" },
                    { icon: "🌡️", title: "Climate Control Integration", desc: "Seamless HVAC system integration for optimal comfort" },
                    { icon: "⚡", title: "Energy Management", desc: "Real-time monitoring and optimization of energy consumption" },
                    { icon: "👥", title: "Occupancy Sensing", desc: "Advanced sensors for space utilization and automation" },
                    { icon: "📱", title: "Mobile App Control", desc: "Remote management through intuitive mobile applications" }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start space-x-4 group">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <span className="text-xl">{feature.icon}</span>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-300 transition-colors">
                          {feature.title}
                        </h4>
                        <p className="text-gray-300">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-400 to-blue-600 rounded-2xl blur opacity-25"></div>
              <img 
                src="/images/home/Light-Current-Systems.jpg" 
                alt="Smart Building Technology" 
                className="relative w-full rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <Section 
        title="Why Choose Our Light Current Services" 
        className="py-16 bg-white"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {lightCurrentFeatures.map((feature) => (
            <Card
              key={feature.index}
              index={feature.index}
              icon={feature.icon}
              title={feature.title}
              desc={feature.desc}
            />
          ))}
        </div>
      </Section>

      {/* Project Gallery */}
      <Section 
        title="Our Light Current Projects" 
        className="py-16 bg-gray-50"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          <div className="relative overflow-hidden rounded-lg shadow-lg group">
            <img 
              src="/images/home/light current home.jpg" 
              alt="Commercial Security Systems" 
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <h4 className="font-semibold">Commercial Buildings</h4>
              <p className="text-sm">Integrated Security & Communication</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-lg shadow-lg group">
            <img 
              src="/images/services/light current.jpg" 
              alt="Smart Building Systems" 
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <h4 className="font-semibold">Smart Buildings</h4>
              <p className="text-sm">Automation & Control Systems</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-lg shadow-lg group">
            <img 
              src="/images/home/Light-Current-Systems.jpg" 
              alt="Data Network Infrastructure" 
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <h4 className="font-semibold">Network Infrastructure</h4>
              <p className="text-sm">Data & Communication Systems</p>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
} 